import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { IntensitiesDataService, IntensitiesColumnsService } from '../../services';
import { DialogIntensityFormComponent } from './intensity-form/intensity-form.component';
import { DialogIntensityDetailsComponent } from './intensity-details/intensity-details.component';
import { Intensity, IntensitiesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-intensities',
  templateUrl: './intensities.component.html',
  styleUrls: ['./intensities.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class IntensitiesComponent implements OnInit, OnDestroy {

  private intensityAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private intensityEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private intensityDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public intensityToDelete: Intensity = {};
  public intensities: Intensity[] = [];
  public intensity: Intensity = {};
  public selectedIntensities: Intensity[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private intensitiesDataService: IntensitiesDataService,
    private intensitiesColumnsService: IntensitiesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('IntensitiesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Intensities - Management');
    this.primengConfig.ripple = true;
    this.getIntensities();
  }

  ngOnDestroy(): void {
    console.log('IntensitiesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getIntensities(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.intensitiesDataService.getIntensities(this.parameters).subscribe((response: IntensitiesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.intensities = response.results;
        } else {
          this.intensities = this.intensities.concat(response.results);
        }
        if (response.inlineCount > this.intensities.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getIntensities();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('INTENSITIESNOTLOADED')});
    });
  }

  importIntensities(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.intensitiesDataService.addIntensities(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('INTENSITIESCREATED')});
                if (proceed === j) {
                  this.getIntensities();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('INTENSITIESNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('INTENSITIESNOTCREATED') + ' ' + msg});
            });
          } else {
            this.intensitiesDataService.updateIntensities(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('INTENSITIESUPDATED')});
                if (proceed === j) {
                  this.getIntensities();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('INTENSITIESNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('INTENSITIESNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.intensitiesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('intensities.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedIntensities() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Intensities?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.intensitiesDataService.deleteIntensities(this.selectedIntensities).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Intensities Deleted', life: 3000});
            this.getIntensities();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Intensities not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Intensities not deleted'});
        });
        this.selectedIntensities = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.intensities, this.columns, 'Intensities');
  }

  exportExcel() {
    ToolsService.exportExcel(this.intensities, this.columns, 'Intensities');
  }

  exportCsv() {
    ToolsService.exportCsv(this.intensities, this.columns, 'Intensities');
  }

  refresh() {
    this.intensitiesDataService.clearCache();
    this.getIntensities();
  }

  intensityAdd(): void {
    if (!this.navigation.getParamValue('intensities.addInDialog', true)) {
      this.router.navigate(['/intensities/add']);
      return;
    }
    this.intensityAddDialog = this.dialogService.open(DialogIntensityFormComponent, {
      header: '', // this.translate.instant('NEWINTENSITY'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        intensity: {id: null},
        users: this.intensitiesColumnsService.users,
      },
    });

    this.intensityAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Intensity added'});
        this.getIntensities();
      }
    });
  }

  intensityEdit(intensity: Intensity): void {
    if (!this.navigation.getParamValue('intensities.editInDialog', true)) {
      this.router.navigate(['/intensities/edit/' + intensity.id]);
      return;
    }
    this.intensityEditDialog = this.dialogService.open(DialogIntensityFormComponent, {
      header: '', // this.translate.instant('EDITINTENSITY') + ' ' + intensity.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        intensity: intensity,
        users: this.intensitiesColumnsService.users,
      },
    });

    this.intensityEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Intensity updated'});
        this.getIntensities();
      }
    });
  }

  intensityDetails(intensity: Intensity): void {
    if (!this.navigation.getParamValue('intensities.detailsInDialog', true)) {
      this.router.navigate(['/intensities/details/' + intensity.id]);
      return;
    }
    this.intensityDetailsDialog = this.dialogService.open(DialogIntensityDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        intensity: intensity,
      }
    });
  }

  intensityDelete(intensity: Intensity): void {
    this.intensityToDelete = intensity;
    this.messageService.clear();
    this.messageService.add({
      key: 'intensityDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + intensity.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.intensitiesDataService.deleteIntensity(this.intensityToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Intensity deleted'});
      this.getIntensities();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Intensity not deleted'});
    });
  }

  onDeleteReject(): void {
    this.intensityToDelete = {};
    this.messageService.clear('intensityDelete');
  }

}
